import { CheckIcon, XIcon, PencilSimpleIcon, TrashIcon, FileXlsIcon } from '@phosphor-icons/react'
import { ScoopDialog, ScoopButton, ScoopText } from '../../../../ui'
import styles from '../APIConnector.module.scss'

export const GooglePickerDialog = ({
    open,
    isDialogHidden,
    selectedFiles,
    APIFeatures,
    objectGroupName,
    errorMessage,
    onClose,
    onBack,
    onOpenPicker,
    onNext,
    onDeleteFile,
}) => {
    const hasFiles = selectedFiles.length > 0
    const dialogTitle = hasFiles
        ? 'Dataset from ' + APIFeatures?.connectorName + ' ' + objectGroupName
        : 'Before You Begin: Prepare Your Data'

    return (
        <ScoopDialog
            open={open && !isDialogHidden}
            title={dialogTitle}
            onClose={onClose}
            actions={
                <>
                    <ScoopButton variant="text" onClick={onBack}>
                        Back
                    </ScoopButton>
                    <ScoopButton onClick={!hasFiles ? onOpenPicker : onNext}>
                        {!hasFiles ? 'Open Google Drive' : 'Next'}
                    </ScoopButton>
                </>
            }
            maxWidth={hasFiles ? 'sm' : 'lg'}
        >
            {!hasFiles && (
                <div className={styles.guideContent}>
                    <ScoopText className={styles.guideSubtitle}>
                        Scoop works best with clean, flat data structures. Your Google Sheet should
                        be a simple table with headers in row 1 and each row representing one
                        record—just like the examples below.
                    </ScoopText>

                    <div className={styles.guideComparison}>
                        {/* Good Example */}
                        <div className={styles.guideExampleSection}>
                            <div className={styles.guideExampleHeader}>
                                <CheckIcon
                                    size={20}
                                    weight="bold"
                                    className={styles.guideCheckIcon}
                                />
                                <ScoopText className={styles.guideGoodTitle}>
                                    GOOD - Clean Data Structure
                                </ScoopText>
                            </div>

                            <div className={styles.guideTableWrapper}>
                                <table className={styles.guideExampleTable}>
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>A</th>
                                            <th>B</th>
                                            <th>C</th>
                                            <th>D</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className={styles.guideGoodHeaderRow}>
                                            <td className={styles.guideRowNumber}>1</td>
                                            <td>Customer Name</td>
                                            <td>Order Date</td>
                                            <td>Amount</td>
                                            <td>Region</td>
                                        </tr>
                                        <tr>
                                            <td className={styles.guideRowNumber}>2</td>
                                            <td>Acme Corp</td>
                                            <td>2024-01-15</td>
                                            <td>$5,000</td>
                                            <td>North</td>
                                        </tr>
                                        <tr>
                                            <td className={styles.guideRowNumber}>3</td>
                                            <td>TechCo</td>
                                            <td>2024-01-16</td>
                                            <td>$3,500</td>
                                            <td>South</td>
                                        </tr>
                                        <tr>
                                            <td className={styles.guideRowNumber}>4</td>
                                            <td>GlobalInc</td>
                                            <td>2024-01-17</td>
                                            <td>$12,000</td>
                                            <td>West</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <ul className={styles.guideGoodList}>
                                <li>
                                    <CheckIcon size={14} weight="bold" /> Headers in row 1
                                </li>
                                <li>
                                    <CheckIcon size={14} weight="bold" /> Consistent formats
                                </li>
                                <li>
                                    <CheckIcon size={14} weight="bold" /> No merged cells
                                </li>
                                <li>
                                    <CheckIcon size={14} weight="bold" /> Clean, flat structure
                                </li>
                            </ul>
                        </div>

                        {/* Bad Example */}
                        <div className={styles.guideExampleSection}>
                            <div className={styles.guideExampleHeader}>
                                <XIcon size={20} weight="bold" className={styles.guideXIcon} />
                                <ScoopText className={styles.guideBadTitle}>
                                    BAD - Avoid These Issues
                                </ScoopText>
                            </div>

                            <div className={styles.guideTableWrapper}>
                                <table className={styles.guideExampleTable}>
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>A</th>
                                            <th>B</th>
                                            <th>C</th>
                                            <th>D</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className={styles.guideRowNumber}>1</td>
                                            <td colSpan={4} className={styles.guideTitleCell}>
                                                Sales Report - Q4 2024
                                            </td>
                                        </tr>
                                        <tr className={styles.guideBadHeaderRow}>
                                            <td className={styles.guideRowNumber}>2</td>
                                            <td>Customer Name</td>
                                            <td>Date</td>
                                            <td>Amount</td>
                                            <td>Region</td>
                                        </tr>
                                        <tr>
                                            <td className={styles.guideRowNumber}>3</td>
                                            <td colSpan={4} className={styles.guideMergedCell}>
                                                Merged Area
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className={styles.guideRowNumber}>4</td>
                                            <td>TechCo</td>
                                            <td>Jan 16</td>
                                            <td className={styles.guideInconsistentCell}>
                                                three thousand
                                            </td>
                                            <td>South</td>
                                        </tr>
                                        <tr>
                                            <td className={styles.guideRowNumber}>5</td>
                                            <td className={styles.guideTotalCell}>TOTAL:</td>
                                            <td></td>
                                            <td>8,500</td>
                                            <td></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <ul className={styles.guideBadList}>
                                <li>
                                    <XIcon size={14} weight="bold" /> Title mixed with data
                                </li>
                                <li>
                                    <XIcon size={14} weight="bold" /> Headers not in row 1
                                </li>
                                <li>
                                    <XIcon size={14} weight="bold" /> Merged cells present
                                </li>
                                <li>
                                    <XIcon size={14} weight="bold" /> Inconsistent data types
                                </li>
                                <li>
                                    <XIcon size={14} weight="bold" /> Subtotal rows included
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className={styles.guideQuickTip}>
                        <ScoopText>
                            <strong>Quick tip:</strong> Your data should look like a simple table
                            with headers in row 1 and each row representing one record.
                        </ScoopText>
                    </div>
                </div>
            )}

            {hasFiles && (
                <div className={styles.dialogContentPadding}>
                    <div className={styles.selectedFileSection}>
                        <ScoopText className={styles.selectedFileLabel}>Selected File:</ScoopText>
                        <ul className={styles.fileList}>
                            {selectedFiles.map((file, index) => (
                                <div key={index} className={styles.fileItem}>
                                    <div className={styles.fileInfo}>
                                        <FileXlsIcon
                                            size={20}
                                            weight="fill"
                                            className={styles.fileIcon}
                                            onClick={() => window.open(file.url, '_blank')}
                                        />
                                        <li className={styles.fileName}>{file.name}</li>
                                    </div>
                                    <div className={styles.fileActions}>
                                        <ScoopButton
                                            variant="icon"
                                            size="xxs"
                                            onClick={onOpenPicker}
                                        >
                                            <PencilSimpleIcon size={16} />
                                        </ScoopButton>
                                        <ScoopButton
                                            variant="icon"
                                            size="xxs"
                                            onClick={() => onDeleteFile(index)}
                                        >
                                            <TrashIcon size={16} className={styles.trashIcon} />
                                        </ScoopButton>
                                    </div>
                                </div>
                            ))}
                        </ul>
                    </div>
                    {errorMessage && (
                        <ScoopText className={styles.errorMessage}>{errorMessage}</ScoopText>
                    )}
                </div>
            )}
        </ScoopDialog>
    )
}
